import { moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { CuratedThreadListLoadAction } from '../../domains/curatedThreadList/actions.types';
import type { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { FeedThreadCountsOnServerIncrementAction } from '../../domains/feedRealtime/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadAction,
  NestedThreadDeepLinkTopLevelLoadAction,
  NestedThreadLoadAction,
  NestedThreadTopLevelRepliesReceivedAction,
  TopLevelMessage,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type { ThreadRepliesLayoutAction } from '../../domains/threadSetLayout/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import type { Message, Thread } from '../types';

import type { TopLevelReplies, TopLevelReplyEdge, TopLevelReplyPages } from './types';
const initialState: TopLevelReplies = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};
type PublishThreadAction =
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction;
export type TopLevelRepliesAction =
  | FeedLoadFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedThreadCountsOnServerIncrementAction
  | NestedThreadLoadAction
  | NestedThreadDeepLinkTopLevelLoadAction
  | NestedThreadDeepLinkSecondLevelLoadAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | PublishReplyMessageFulfilledAction
  | NestedThreadTopLevelRepliesReceivedAction
  | PublishThreadAction
  | PublishEditMessageFulfilledAction
  | ThreadRepliesLayoutAction
  | DeleteMessageFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | CuratedThreadListLoadAction;
const containsTopLevelReplyEdgeWithNodeId = (topLevelReplyEdges: TopLevelReplyEdge[], nodeId: Thread['id']) =>
  topLevelReplyEdges.findIndex((edge) => edge.nodeId === nodeId) > -1;
const topLevelReplyEdgesWithoutLoadedDuplicates = (
  existingEdges: TopLevelReplyEdge[],
  payloadEdges: TopLevelReplyEdge[]
) => payloadEdges.filter((edge) => !containsTopLevelReplyEdgeWithNodeId(existingEdges, edge.nodeId));
const stateWithPreviousPage = (
  state: TopLevelReplies,
  payload: TopLevelRepliesLoadPreviousFulfilledAction['payload']
): TopLevelReplies => {
  const {
    entities: { topLevelReplies },
    threadId,
  } = payload;
  const {
    hasPreviousPage,
    totalPreviousCount,
    totalUnseenPreviousCount,
    edges: previousEdges,
    startCursor,
  } = topLevelReplies[threadId];
  const { edges: existingEdges, newMessagesFromCurrentUser } = state.fulfilled.byId[threadId];
  const messagesFromCurrentUserWithoutPagedMessages = newMessagesFromCurrentUser.filter(
    (messageId) => !previousEdges.find((edge) => edge.nodeId === messageId)
  );
  const previousEdgesWithoutDuplicates = topLevelReplyEdgesWithoutLoadedDuplicates(existingEdges, previousEdges);
  const updatedValues = {
    startCursor,
    hasPreviousPage,
    totalPreviousCount,
    totalUnseenPreviousCount,
    edges: [...previousEdgesWithoutDuplicates, ...existingEdges],
    newMessagesFromCurrentUser: messagesFromCurrentUserWithoutPagedMessages,
  };

  return update(state, threadId, updatedValues);
};
const stateWithNextPage = (
  state: TopLevelReplies,
  payload: TopLevelRepliesLoadNextFulfilledAction['payload']
): TopLevelReplies => {
  const {
    entities: { topLevelReplies },
    threadId,
  } = payload;
  const { hasNextPage, totalNextCount, totalUnseenNextCount, edges: nextEdges, endCursor } = topLevelReplies[threadId];
  const { edges: existingEdges, newMessagesFromCurrentUser } = state.fulfilled.byId[threadId];
  const messagesFromCurrentUserWithoutPagedMessages = newMessagesFromCurrentUser.filter(
    (messageId) => !nextEdges.find((edge) => edge.nodeId === messageId)
  );
  const nextEdgesWithoutDuplicates = topLevelReplyEdgesWithoutLoadedDuplicates(existingEdges, nextEdges);
  const newEdges = [...existingEdges, ...nextEdgesWithoutDuplicates];
  const updatedValues: Partial<TopLevelReplyPages> = {
    endCursor,
    hasNextPage,
    totalNextCount,
    totalUnseenNextCount,
    edges: newEdges,
    firstLoadedEdges: newEdges.slice(-2),
    newMessagesFromCurrentUser: messagesFromCurrentUserWithoutPagedMessages,
    realtimeMessageIds: new Set(),
  };

  return update(state, threadId, updatedValues);
};
const stateWithNewReply = (state: TopLevelReplies, payload: PublishReplyMessageFulfilledAction['payload']) => {
  const { isSecondLevelReply, threadId, messageId } = payload;
  const topLevelReplies = state.fulfilled.byId[threadId];
  if (isSecondLevelReply || topLevelReplies == null) {
    return state;
  }
  const { newMessagesFromCurrentUser } = topLevelReplies;

  return update(state, threadId, {
    newMessagesFromCurrentUser: [...newMessagesFromCurrentUser, messageId],
    totalCount: topLevelReplies.totalCount + 1,
  });
};
const stateWithNewRepliesCount = (
  state: TopLevelReplies,
  payload: FeedThreadCountsOnServerIncrementAction['payload']
) => {
  if (payload.threadLevel !== 1) {
    return state;
  }

  return stateWithRealtimeReplies(state, [{ messageId: payload.messageId, threadId: payload.threadId }]);
};
const stateWithRealtimeReplies = (state: TopLevelReplies, realtimeMessages: TopLevelMessage[]) =>
  realtimeMessages.reduce((updatedState: TopLevelReplies, newTopLevelMessage: TopLevelMessage) => {
    const topLevelMessagesForThread = state.fulfilled.byId[newTopLevelMessage.threadId];
    if (topLevelMessagesForThread == null) {
      return state;
    }
    const { messageId, threadId } = newTopLevelMessage;
    const { newMessagesFromCurrentUser, realtimeMessageIds, edges, repliesSortedBy } = topLevelMessagesForThread;
    const isFromCurrentUser = newMessagesFromCurrentUser.includes(messageId);
    const isPaginatedEdge = edges.findIndex((edge) => edge.nodeId === messageId) > -1;
    const isVoteOrderedThread = repliesSortedBy === 'UPVOTE_RANK_THEN_CREATED_AT';
    if (isFromCurrentUser || isPaginatedEdge || isVoteOrderedThread) {
      return state;
    }
    const updatedRealtimeMessageIds = new Set(realtimeMessageIds.add(messageId));

    return update(updatedState, threadId, { realtimeMessageIds: updatedRealtimeMessageIds });
  }, state);
const stateWithNewThread = (state: TopLevelReplies, { threadId, entities }: PublishThreadAction['payload']) => {
  const thread = entities.threads[threadId];
  if (!thread) {
    return state;
  }
  const { repliesSortedBy } = thread;

  return moveToFulfilled(state, {
    [threadId]: {
      id: threadId,
      startCursor: null,
      hasPreviousPage: false,
      totalPreviousCount: 0,
      totalUnseenPreviousCount: 0,
      endCursor: null,
      hasNextPage: false,
      totalCount: 0,
      totalNextCount: 0,
      totalUnseenNextCount: 0,
      edges: [],
      firstLoadedEdges: [],
      newMessagesFromCurrentUser: [],
      realtimeMessageIds: new Set<Message['id']>(),
      repliesSortedBy,
    },
  });
};
const stateWithEditedThread = (state: TopLevelReplies, threadId: Thread['id']): TopLevelReplies =>
  moveToFulfilled(state, {
    [threadId]: {
      id: threadId,
      startCursor: null,
      hasPreviousPage: false,
      totalPreviousCount: 0,
      totalUnseenPreviousCount: 0,
      endCursor: null,
      hasNextPage: false,
      totalCount: 0,
      totalNextCount: 0,
      totalUnseenNextCount: 0,
      edges: [],
      firstLoadedEdges: [],
      newMessagesFromCurrentUser: [],
      realtimeMessageIds: new Set<Message['id']>(),
      repliesSortedBy: 'CREATED_AT',
    },
    ...state.fulfilled.byId,
  });
const stateWithNewThreadsTracked = (
  state: TopLevelReplies,
  topLevelReplies: Record<Message['id'], TopLevelReplyPages> | undefined
) => moveToFulfilled(state, { ...topLevelReplies, ...state.fulfilled.byId });
const stateWithTopLevelMessageForThreadRemoved = (
  state: TopLevelReplies,
  threadId: Thread['id'],
  topLevelMessageId: Message['id']
) => {
  const secondLevelRepliesPageForThread = state.fulfilled.byId[threadId];
  if (!secondLevelRepliesPageForThread) {
    return state;
  }
  const edgesWithoutMessage = secondLevelRepliesPageForThread.edges.filter((edge) => edge.nodeId !== topLevelMessageId);
  const newMessagesFromCurrentUserWithoutMessage = secondLevelRepliesPageForThread.newMessagesFromCurrentUser.filter(
    (messageId) => messageId !== topLevelMessageId
  );

  return update(state, threadId, {
    edges: edgesWithoutMessage,
    newMessagesFromCurrentUser: newMessagesFromCurrentUserWithoutMessage,
    totalCount: edgesWithoutMessage.length,
  });
};
const stateWithFirstLoadedEdges = (state: TopLevelReplies, { threadId }: ThreadRepliesLayoutAction['payload']) => {
  const topLevelMessagesForThread = state.fulfilled.byId[threadId];
  if (topLevelMessagesForThread == null) {
    return state;
  }

  if (topLevelMessagesForThread.firstLoadedEdges.length !== 0) {
    return state;
  }

  return update(state, threadId, { firstLoadedEdges: topLevelMessagesForThread.edges });
};
const deleteMessage = (
  state: TopLevelReplies,
  { threadId, messageId, deletionOutcome, threadLevel, parentMessageId }: DeleteMessageFulfilledAction['payload']
) => {
  const wasTopLevelReplyRemoved = deletionOutcome === 'MESSAGE_REMOVED' && threadLevel === 'TOP_LEVEL_REPLY';
  if (wasTopLevelReplyRemoved) {
    return stateWithTopLevelMessageForThreadRemoved(state, threadId, messageId);
  }
  const wasSecondLevelReplyAndParentRemoved =
    deletionOutcome === 'MESSAGE_AND_PARENT_REMOVED' && threadLevel === 'SECOND_LEVEL_REPLY';
  if (wasSecondLevelReplyAndParentRemoved && parentMessageId != null) {
    return stateWithTopLevelMessageForThreadRemoved(state, threadId, parentMessageId);
  }

  return state;
};
const reducer: Reducer<TopLevelReplies, TopLevelRepliesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithNewThreadsTracked(state, action.payload.entities.topLevelReplies);
    case 'FEED_THREAD_COUNTS_ON_SERVER.INCREMENT':
      return stateWithNewRepliesCount(state, action.payload);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return stateWithPreviousPage(state, action.payload);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithPreviousPage(state, action.payload);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithNextPage(state, action.payload);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return stateWithNewReply(state, action.payload);
    case 'NESTED_THREAD.TOP_LEVEL_REPLIES_RECEIVED':
      return stateWithRealtimeReplies(state, action.payload.messages);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return stateWithNewThread(state, action.payload);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return stateWithEditedThread(state, action.payload.threadId);
    case 'MESSAGE.DELETE_FULFILLED':
      return deleteMessage(state, action.payload);
    case 'THREAD.REPLIES_LAYOUT_SET':
      return stateWithFirstLoadedEdges(state, action.payload);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    case 'CURATED_THREAD_LIST_LOAD.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topLevelReplies);
    default:
      return state;
  }
};
export default reducer;
